import { cacheService } from './cacheService';
import { supabase } from '@/integrations/supabase/client';

export class DefaultImageService {
  private static instance: DefaultImageService;
  private readonly CACHE_KEY = 'default_ai_image';
  private readonly CACHE_VERSION = '1.0.0';
  private readonly CACHE_TTL_MS = 24 * 60 * 60 * 1000; // 24 hours
  private readonly DB_SETTING_KEY = 'default_image_ai_solution';

  // Fallback image URL (in case database read fails)
  private readonly FALLBACK_IMAGE_URL = 'https://nwweejpidselzqtwvgnb.supabase.co/storage/v1/object/public/aizen-pub-bucket/solution-images/1753258100585-ujntdr.png';

  private constructor() {}

  public static getInstance(): DefaultImageService {
    if (!DefaultImageService.instance) {
      DefaultImageService.instance = new DefaultImageService();
    }
    return DefaultImageService.instance;
  }

  /**
   * Get the default AI image URL with 24-hour caching (reads from settings table)
   */
  public async getDefaultImageUrl(): Promise<string> {
    return await cacheService.get<string>(
      this.CACHE_KEY,
      async () => {
        try {
          const { data, error } = await supabase
            .from('settings')
            .select('value')
            .eq('key', this.DB_SETTING_KEY)
            .single();
            
          if (error || !data) {
            console.warn(`Failed to fetch ${this.DB_SETTING_KEY} setting:`, error);
            return this.FALLBACK_IMAGE_URL;
          }
          
          return data.value;
        } catch (error) {
          console.error(`Error fetching ${this.DB_SETTING_KEY}:`, error);
          return this.FALLBACK_IMAGE_URL;
        }
      },
      {
        ttlMs: this.CACHE_TTL_MS,
        version: this.CACHE_VERSION
      }
    );
  }

  /**
   * Get the default AI image URL synchronously (from cache only)
   * Falls back to hardcoded URL if cache is empty
   */
  public getDefaultImageUrlSync(): string {
    const cachedUrl = cacheService.getSync<string>(this.CACHE_KEY, {
      version: this.CACHE_VERSION,
      ttlMs: this.CACHE_TTL_MS
    });

    return cachedUrl || this.FALLBACK_IMAGE_URL;
  }

  /**
   * Initialize cache by loading from database (call this on app startup)
   */
  public async initializeCache(): Promise<void> {
    try {
      await this.getDefaultImageUrl(); // This will load and cache the value
      console.log('Default image cache initialized successfully');
    } catch (error) {
      console.warn('Failed to initialize default image cache:', error);
    }
  }

  /**
   * Update the default image URL in database and cache
   */
  public async updateDefaultImageUrl(newUrl: string): Promise<void> {
    try {
      const { error } = await supabase
        .from('settings')
        .upsert({
          key: this.DB_SETTING_KEY,
          value: newUrl,
          description: 'Default image URL for AI solutions when no specific image is provided. Used as fallback image across the application.'
        });

      if (error) throw error;

      // Update cache immediately
      cacheService.set(this.CACHE_KEY, newUrl, {
        ttlMs: this.CACHE_TTL_MS,
        version: this.CACHE_VERSION
      });

      console.log('Default image URL updated successfully');
    } catch (error) {
      console.error('Error updating default image URL:', error);
      throw error;
    }
  }

  /**
   * Clear the cache (useful for testing or manual refresh)
   */
  public clearCache(): void {
    cacheService.clear(this.CACHE_KEY);
  }
}

// Export singleton instance for easy access
export const defaultImageService = DefaultImageService.getInstance(); 